import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import RecoveryRing from "@/components/RecoveryRing";
import MetricTile from "@/components/MetricTile";
import { ArrowUpRight, Sparkles, Watch, Trophy, ShoppingBag } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [week, setWeek] = useState([]);
  const [today, setToday] = useState(null);
  const [insight, setInsight] = useState(null);
  const [stack, setStack] = useState([]);
  const [rec, setRec] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [providers, setProviders] = useState(null);

  const loadAll = async () => {
    setLoaded(false);
    try {
      const [w, s, c, p] = await Promise.all([
        api.get("/wearables/snapshots/week"),
        api.get("/supplements/stack"),
        api.get("/challenges/my"),
        api.get("/wearables/providers"),
      ]);
      setWeek(w.data);
      const todayISO = new Date().toISOString().slice(0, 10);
      setToday(w.data.find((d) => d.date === todayISO) || null);
      setStack(s.data);
      setChallenges(c.data);
      setProviders(p.data);
    } catch (e) {
      toast.error("Couldn't load dashboard data");
    } finally {
      setLoaded(true);
    }
    try {
      const rr = await api.get("/supplements/recommendation/today");
      setRec(rr.data);
    } catch {}
    try {
      const g = await api.get("/flyn/greeting");
      setInsight(g.data.message);
    } catch {
      setInsight(null);
    }
  };

  useEffect(() => { loadAll(); }, []);

  if (!loaded) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="eyebrow">LOADING TODAY…</div>
      </div>
    );
  }

  const trend = (key) => week.map((d) => d[key]);
  const whoopConnected = providers?.whoop?.status === "connected";
  const activeChallenges = challenges.filter((c) => c.membership?.status === "active");

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">TODAY · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase mt-2 leading-none" data-testid="dashboard-greeting">
            Hello, {user?.name?.split(" ")[0] || "you"}.
          </h1>
          <div className="mt-3 font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-3">
            {whoopConnected ? (
              <span className="lime-dot text-obsidian" data-testid="dash-whoop-status">WHOOP · SYNCED</span>
            ) : (
              <span data-testid="dash-whoop-status">NO WEARABLE CONNECTED</span>
            )}
            <span>·</span>
            <span data-testid="dash-active-challenges">
              {activeChallenges.length} ACTIVE {activeChallenges.length === 1 ? "STAKE" : "STAKES"}
            </span>
          </div>
        </div>
        <Link to="/app/coach" className="btn-outline-ink" data-testid="dash-open-coach">
          Ask FLYN <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {/* EMPTY STATE — no snapshots and no active challenges */}
      {!today && (
        <EmptyState />
      )}

      {/* FLYN insight card — only when we have data */}
      {today && insight && (
        <div className="panel p-7 rounded-2xl border-l-4 border-l-lime" data-testid="flyn-insight">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-obsidian text-lime flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="eyebrow mb-2 text-obsidian">FLYN · TODAY'S READ</div>
              <p className="text-lg leading-relaxed text-neutral-800">{insight}</p>
            </div>
          </div>
        </div>
      )}

      {/* Metrics */}
      {today && (
        <div className="grid md:grid-cols-12 gap-6" data-testid="dash-metrics">
          <div className="md:col-span-5 panel p-8 rounded-2xl flex flex-col items-center justify-center">
            <RecoveryRing value={today.recovery_pct || 0} />
            <div className="mt-6 text-center">
              <div className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase">
                {today.source === "whoop" ? "WHOOP · Live" : "Demo Data · Connect WHOOP for live"}
              </div>
            </div>
          </div>

          <div className="md:col-span-7 grid grid-cols-2 gap-4">
            <MetricTile label="HRV" value={today.hrv_ms?.toFixed?.(0) ?? "—"} unit="ms" trend={trend("hrv_ms")} testId="metric-hrv" />
            <MetricTile label="Resting HR" value={today.rhr_bpm?.toFixed?.(0) ?? "—"} unit="bpm" trend={trend("rhr_bpm")} testId="metric-rhr" />
            <MetricTile label="Sleep Score" value={today.sleep_score?.toFixed?.(0) ?? "—"} unit="/100" trend={trend("sleep_score")} testId="metric-sleep-score" />
            <MetricTile label="Strain" value={today.strain?.toFixed?.(1) ?? "—"} trend={trend("strain")} color="#C8F135" testId="metric-strain" />
          </div>
        </div>
      )}

      {/* Active challenges */}
      {activeChallenges.length > 0 && (
        <div data-testid="dash-challenge-section">
          <div className="eyebrow mb-3">ACTIVE STAKES</div>
          <div className="grid md:grid-cols-2 gap-4">
            {activeChallenges.map((c) => (
              <Link
                to="/app/challenges"
                key={c.membership.id}
                data-testid={`dash-challenge-${c.membership.id}`}
                className="panel p-6 rounded-2xl hover:-translate-y-0.5 transition-transform"
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{c.challenge?.name}</div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.25em] bg-lime text-obsidian px-2 py-1 rounded-full">ACTIVE</span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-display text-4xl">${c.membership.stake_amount.toFixed(0)}</span>
                  <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">→ {c.charity?.name?.slice(0, 24)}</span>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <div>
                    <span className="font-display text-2xl">{c.membership.days_completed}</span>
                    <span className="text-neutral-500 font-mono text-xs ml-1">/ {c.challenge?.duration_days || 75} DAYS</span>
                  </div>
                  <span className="text-xs text-neutral-500 font-mono tracking-widest uppercase">tap to open →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recommendation card */}
      {today && rec?.recommendation && rec.product && (
        <div className="panel p-7 rounded-2xl" data-testid="recommendation-card">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="eyebrow mb-2">FLYN · STACK SIGNAL</div>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-display text-4xl">{rec.product.name}</span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">{rec.product.tag}</span>
              </div>
              <p className="text-neutral-700 leading-relaxed">{rec.recommendation.reasoning}</p>
            </div>
            <Link to="/app/shop" className="btn-lime shrink-0" data-testid="rec-go-shop">
              View in Stack
            </Link>
          </div>
        </div>
      )}

      {/* My stack widget */}
      <div className="panel p-7 rounded-2xl" data-testid="my-stack">
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">MY STACK</div>
          <Link to="/app/shop" className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-black">
            {stack.length ? "Reorder / Build →" : "Build your first stack →"}
          </Link>
        </div>
        {stack.length === 0 ? (
          <div className="text-neutral-500 text-sm" data-testid="my-stack-empty">
            No supplements in your stack yet.{" "}
            <Link to="/app/shop" className="underline text-black">Browse the shop.</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {stack.map((p) => (
              <div key={p.id} className="border border-neutral-200 rounded-xl p-4">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                <div className="font-display text-2xl mt-2 leading-none">{p.name}</div>
                <div className="text-xs text-neutral-500 mt-1">{p.subtitle}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="panel p-10 md:p-14 rounded-3xl text-center" data-testid="dashboard-empty">
      <div className="relative inline-flex mb-6">
        <div className="absolute inset-0 lime-glow rounded-full opacity-60" />
        <div className="relative w-20 h-20 rounded-full bg-obsidian text-lime flex items-center justify-center">
          <Watch className="w-9 h-9" />
        </div>
      </div>
      <div className="eyebrow mb-3">NO BIOMETRIC DATA YET</div>
      <h2 className="font-display text-4xl md:text-5xl uppercase leading-none mb-4">
        Connect your WHOOP<br/>to bring today alive.
      </h2>
      <p className="text-neutral-600 max-w-md mx-auto mb-8 leading-relaxed">
        FLYN, your stack, and stake verification all read directly from your wearable.
        Real data only — plug in your WHOOP to start.
      </p>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <Link to="/app/wearables" className="btn-lime" data-testid="empty-connect-whoop">
          <Watch className="w-4 h-4" /> Connect WHOOP
        </Link>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-4 text-left">
        <Link to="/app/challenges" className="border border-neutral-200 rounded-2xl p-6 hover:border-obsidian transition-colors" data-testid="empty-start-stake">
          <Trophy className="w-6 h-6 mb-3" />
          <div className="font-display text-2xl uppercase leading-none">Start a Stake</div>
          <div className="text-sm text-neutral-600 mt-2">Lock money on 75 Hard. Fail → charity. Win → yourself.</div>
        </Link>
        <Link to="/app/shop" className="border border-neutral-200 rounded-2xl p-6 hover:border-obsidian transition-colors" data-testid="empty-browse-shop">
          <ShoppingBag className="w-6 h-6 mb-3" />
          <div className="font-display text-2xl uppercase leading-none">Browse Stack</div>
          <div className="text-sm text-neutral-600 mt-2">4 supplements, each paired to a metric. Recovery, Sleep, Perform, Baseline.</div>
        </Link>
      </div>
    </div>
  );
}
